import React, { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { IconSettings } from '@tabler/icons-react'
import { Checkbox, Tooltip, Tree, TreeDataNode, TreeProps } from 'antd'
import styled from 'styled-components'

import {
  LocationActionsProps,
  LocationEmptyStateProps,
  LocationLoadingProps,
} from './types'
import { findNode } from './utils'

import { Button } from '@/components/shared-components/BUI/Button'
import { DozerLoading } from '@/components/shared-components/DozerLoading'
import { GET_PROJECT_LOCATIONS } from '@/modules/projects/graphQL'
import { routes } from '@/utils/routes'

import {
  ProjectLocationsUiQuery,
  ProjectLocationsUiQueryVariables,
} from 'types/api.graphql'

export const Wrapper = styled.div`
  min-height: 4rem;
`

export const InnerWrapper = styled.div`
  padding: 0.5rem 1rem 0.25rem 0.5rem;
`

export const Configure = styled.a`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`

export const Clear = styled(Button)`
  display: block;
  margin-left: auto;
`

export const Label = styled.span`
  color: ${({ theme }) => theme.colour.secondary.base};
`

export const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 1rem 0.25rem 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colour.neutral.border};
`

export type Location = {
  id: string
  name: string
  children?: Location[]
}

export const convertLocationToNode = (location: Location): TreeDataNode => ({
  key: location.id,
  title: location.name,
  children: location.children
    ? convertLocationsToTree(location.children)
    : undefined,
})

export const convertLocationsToTree = (
  locations: Location[]
): TreeDataNode[] => {
  return locations.map(convertLocationToNode)
}

export const LocationLoading: React.FC<LocationLoadingProps> = ({
  loading,
}) => (
  <Wrapper>
    <InnerWrapper>
      <DozerLoading loading={loading} size={24} />
    </InnerWrapper>
  </Wrapper>
)

export const LocationEmptyState: React.FC<LocationEmptyStateProps> = ({
  projectId,
  canConfigure,
}) => (
  <Wrapper>
    <InnerWrapper>
      No locations found...
      {canConfigure && (
        <Configure
          target="_blank"
          rel="noreferrer"
          href={routes.projects.view(projectId, { tab: 'locations' })}
        >
          <IconSettings size={16} />
          Configure locations
        </Configure>
      )}
    </InnerWrapper>
  </Wrapper>
)

export const LocationActions: React.FC<LocationActionsProps> = ({
  showIncludeSublocations,
  includeSublocations,
  hasValue,
  onChangeIncludeSublocations,
  onClear,
}) => (
  <ActionsWrapper>
    {showIncludeSublocations && (
      <Tooltip
        title={
          !hasValue
            ? 'Select a location first'
            : 'Include all locations under this location'
        }
      >
        <Checkbox
          checked={includeSublocations}
          onChange={onChangeIncludeSublocations}
          disabled={!hasValue}
        >
          <Label>Sublocations</Label>
        </Checkbox>
      </Tooltip>
    )}
    <Clear variant="secondary-text" size="small" onClick={onClear}>
      Clear
    </Clear>
  </ActionsWrapper>
)

type ProjectLocationTreeProps = TreeProps & {
  projectId: string
  value?: string
  onChange?: (value: string, node: TreeDataNode) => void
}

export const ProjectLocationTree: React.FC<ProjectLocationTreeProps> = ({
  projectId,
  value,
  onChange,
  children,
  ...treeProps
}) => {
  const { data, loading } = useQuery<
    ProjectLocationsUiQuery,
    ProjectLocationsUiQueryVariables
  >(GET_PROJECT_LOCATIONS, { variables: { projectId } })

  const treeData = useMemo(() => {
    if (!data?.projectLocations) return []
    return convertLocationsToTree(data.projectLocations)
  }, [data])

  if (loading) {
    return <DozerLoading loading size={24} />
  }

  if (!treeData.length) {
    return (
      <>
        No locations found...
        <Configure
          target="_blank"
          rel="noreferrer"
          href={routes.projects.view(projectId, { tab: 'locations' })}
        >
          <IconSettings size={16} />
          Configure locations
        </Configure>
      </>
    )
  }

  return (
    <>
      <Tree
        showLine
        treeData={treeData}
        onSelect={(keys) =>
          onChange?.(keys[0] as string, findNode(keys[0] as string, treeData))
        }
        selectedKeys={[value]}
        {...treeProps}
      />
      {children}
    </>
  )
}
